import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import request from 'lib/request';
import type { PromptsResponse, PromptTextResponse } from './types';

// Which prompt the view panel shows; null when the panel is closed.
export interface PromptSelection {
  ref: number | string;
  title: string;
}
export const selectionAtom = atom<PromptSelection | null>(null);

// The whole feed, loaded once on mount. loadable() surfaces loading/error
// without suspense (mirrors the agent-costs admin page).
const promptsBaseAtom = atom(async () => {
  const response = await request.get<PromptsResponse>('/admin/prompts');
  if ('error' in response) throw new Error(response.error);
  return response;
});
const promptsLoadableAtom = loadable(promptsBaseAtom);
export const promptsAtom = atom((get) => get(promptsLoadableAtom));

// The selected prompt's full text — refetches when the selection changes and
// stays null (no request) while the panel is closed.
const promptTextBaseAtom = atom(async (get) => {
  const selection = get(selectionAtom);
  if (selection === null) return null;

  const response = await request.get<PromptTextResponse>(
    `/admin/prompts/${encodeURIComponent(String(selection.ref))}`
  );
  if ('error' in response) throw new Error(response.error);
  return response;
});
const promptTextLoadableAtom = loadable(promptTextBaseAtom);
export const promptTextAtom = atom((get) => get(promptTextLoadableAtom));

// Loadable error → message string (or null when not errored). Wraps the
// `error instanceof Error` guard shared by the page and the view panel.
type LoadableState =
  | { state: 'loading' }
  | { state: 'hasError'; error: unknown }
  | { state: 'hasData' };

export function loadableErrorMessage(
  loadableState: LoadableState,
  fallback: string
): string | null {
  if (loadableState.state !== 'hasError') return null;
  return loadableState.error instanceof Error
    ? loadableState.error.message
    : fallback;
}
